/**
 * Tier + probe taxonomy and per-tier configuration.
 *
 * Tier is a property of the MATCH, not the player (spec section 3) — both
 * seats always play the same tier, and ratings/boards are scoped per tier.
 */

import { SCORING, type PinpointAttemptPolicy } from './scoring';

/**
 * Four, in difficulty order. SURVEYOR is the extra-hard country tier: the
 * same pool and clock family as Cartographer, but rung 1 no longer names the
 * country -- it DESCRIBES it, the way Cities and US States already do at their
 * top tier, and the player has to work out which country is meant before the
 * search on the globe can even begin.
 */
export const TIERS = ['explorer', 'navigator', 'cartographer', 'surveyor'] as const;
export type Tier = (typeof TIERS)[number];

export const PROBE_TYPES = [
  'capital',
  'population',
  'flag',
  'borders',
  'languages',
  'region',
  /**
   * VARIETY PASS -- four more multiple-choice shapes, so a Navigator ladder is
   * no longer "statement, bracket, flag" seven rounds running.
   *
   *   neighbour  which of these four countries shares a land border with X
   *   currency   which of these four units is X's money
   *   language   which of these four languages is widely spoken in X
   *   populous   which of these four countries has more (or fewer) people than X
   *
   * All four are ordinary four-option choices graded by `correctIndex`, so
   * nothing on the wire or in the grader changes shape; only the ladder
   * builder's bands and the prompt card's kicker know the new names.
   */
  'neighbour',
  'currency',
  'language',
  'populous',
  /**
   * CHANGE 5 — rung 2's true/false statement probe. A claim about the target
   * country ("Ottawa is the capital of Canada", "Chad is landlocked") that the
   * player answers true or false.
   *
   * Its 50% blind floor against rung 2's x2 is DELIBERATE and EV-neutral; the
   * reasoning is recorded in full in `scoring.ts`. Do not "fix" it.
   */
  'truefalse',
  /**
   * CHANGE 6 — the golden round's open-ended NUMERIC answer. Drawn ONLY on the
   * terminal rung of a golden round; a ladder builder that offers it anywhere
   * else has a bug, and `@geogame/rules` asserts as much.
   *
   * NUMBERS ONLY, never free text: spelling, diacritics and Ivory Coast /
   * Cote d'Ivoire make text answers a normalisation swamp. See
   * `NUMERIC_QUANTITIES` in `protocol.ts` for the quantities it may ask about.
   */
  'numeric',
] as const;
export type ProbeType = (typeof PROBE_TYPES)[number];

/** Rung 1 is always the globe pinpoint. Rungs 2..N are knowledge probes. */
export const PINPOINT_RUNG = 1;

export interface TierConfig {
  /** Total rungs including the rung-1 pinpoint. Explorer stops at 3. */
  readonly ladderDepth: number;
  /** Answer window for a single rung. Timeout on a rung === bust (spec 2). */
  readonly secondsPerRung: number;
  /** Bank-or-push window. Timeout here === auto-bank, never a bust (spec 2). */
  readonly decisionSeconds: number;
  /** Pixel radius the globe snaps a tap to the nearest country within. */
  readonly snapRadiusPx: number;
  /** How many countries, ordered by fameRank, form the scored pool. */
  readonly countryPoolSize: number;
  /** Whether sub-1000 km2 states may be drawn. */
  readonly allowMicrostates: boolean;
  /** Probes legal at this tier. */
  readonly probeTypes: readonly ProbeType[];
  /** Pre-orient the globe to the target's region before the reveal. */
  readonly orientToRegion: boolean;
  /**
   * CHANGE 4. How many rung-1 pinpoint attempts a seat gets, and what each one
   * pays. `multipliers.length === maxAttempts` by construction.
   *
   * Scoped to the TIER rather than to a new game mode, because the tier axis
   * already exists and a mode axis would multiply queues and leaderboards for
   * no gain. EXPLORER gets three: failing rung 1 is the worst failure in the
   * game — it denies the player the interesting decision entirely, they never
   * get to bet — and Explorer is exactly the population that fails it. The two
   * upper tiers keep the single attempt: at Navigator's 28px and
   * Cartographer's 14px snap radius, a retry allowance is a precision aid
   * rather than an accessibility one.
   *
   * Both seats always get the same allowance, because tier is a property of
   * the match (invariant 1: nothing here can differ between seats).
   */
  readonly pinpointAttempts: PinpointAttemptPolicy;
  /**
   * CHANGE 6. How many rounds per match are GOLDEN. Exactly one, everywhere;
   * the field exists so the count is named rather than assumed, and so a
   * course or tier could vary it later without a protocol change.
   *
   * WHICH round is golden is NOT configured here — it is derived from the
   * MATCH SEED (`goldenRoundIndex` in `@geogame/rules`), because both seats
   * must hit it on the same round index or the match is unfair and invariant 1
   * is broken.
   */
  readonly goldenRoundsPerMatch: number;
}

/** The full three-attempt ladder, as Explorer uses it. */
const EXPLORER_PINPOINT_ATTEMPTS: PinpointAttemptPolicy = {
  maxAttempts: SCORING.PINPOINT_ATTEMPT_MULTIPLIERS.length,
  multipliers: SCORING.PINPOINT_ATTEMPT_MULTIPLIERS,
};

/** One attempt, full value — the pre-change-4 behaviour, kept for the upper tiers. */
const SINGLE_PINPOINT_ATTEMPT: PinpointAttemptPolicy = {
  maxAttempts: 1,
  multipliers: [SCORING.PINPOINT_ATTEMPT_MULTIPLIERS[0] ?? 1],
};

export const TIER_CONFIG: Readonly<Record<Tier, TierConfig>> = {
  explorer: {
    ladderDepth: 3,
    secondsPerRung: 15,
    decisionSeconds: 8,
    snapRadiusPx: 48,
    countryPoolSize: 50,
    allowMicrostates: false,
    // `currency` joins the hard band here so Explorer's terminal rung is not
    // a flag question every single round; the mediums are legal only as
    // fallbacks, since a three-rung ladder has no medium rung.
    probeTypes: [
      'capital',
      'flag',
      'currency',
      'region',
      'neighbour',
      'language',
      'populous',
      'truefalse',
      'numeric',
    ],
    orientToRegion: true,
    pinpointAttempts: EXPLORER_PINPOINT_ATTEMPTS,
    goldenRoundsPerMatch: SCORING.GOLDEN_ROUNDS_PER_MATCH,
  },
  navigator: {
    ladderDepth: 4,
    secondsPerRung: 10,
    decisionSeconds: 6,
    snapRadiusPx: 28,
    countryPoolSize: 120,
    // Permitted, but a 120-deep fame pool means microstates surface rarely.
    allowMicrostates: true,
    probeTypes: [
      'capital',
      'flag',
      'currency',
      'region',
      'population',
      'borders',
      'neighbour',
      'language',
      'populous',
      'truefalse',
      'numeric',
    ],
    orientToRegion: false,
    pinpointAttempts: SINGLE_PINPOINT_ATTEMPT,
    goldenRoundsPerMatch: SCORING.GOLDEN_ROUNDS_PER_MATCH,
  },
  cartographer: {
    ladderDepth: 4,
    secondsPerRung: 8,
    decisionSeconds: 5,
    snapRadiusPx: 14,
    countryPoolSize: 1000,
    allowMicrostates: true,
    probeTypes: [
      'capital',
      'flag',
      'currency',
      'region',
      'population',
      'borders',
      'neighbour',
      'language',
      'populous',
      'languages',
      'truefalse',
      'numeric',
    ],
    orientToRegion: false,
    pinpointAttempts: SINGLE_PINPOINT_ATTEMPT,
    goldenRoundsPerMatch: SCORING.GOLDEN_ROUNDS_PER_MATCH,
  },
  /**
   * SURVEYOR -- Cartographer with the name taken away.
   *
   * Same pool (everything, microstates included), same 14px snap, same
   * four-rung ladder with every probe legal. What changes is rung 1: the
   * prompt is a one-sentence CLUE from `@geogame/data/countryclues` rather
   * than the country's name, so "find Bhutan" becomes "find the Himalayan
   * kingdom that measures Gross National Happiness".
   *
   * THE CLOCK IS TWO SECONDS KINDER THAN CARTOGRAPHER'S, NOT HARSHER. The
   * difficulty of this tier is meant to live in the clue, and a clue has to
   * be read, decoded and THEN found; the state pool's clue rung was measured
   * at "borderline impossible" on 8 seconds even with the reading allowance,
   * and a country clue is one step more indirect than a state clue. Rung 1
   * also earns `PROMPT_READING_SECONDS` on top, exactly as a state clue does,
   * because every clue is longer than `NAME_PROMPT_MAX_CHARS`
   * (`countryclues.test.ts` holds that line).
   */
  surveyor: {
    ladderDepth: 4,
    secondsPerRung: 10,
    decisionSeconds: 6,
    snapRadiusPx: 14,
    countryPoolSize: 1000,
    allowMicrostates: true,
    probeTypes: [
      'capital',
      'flag',
      'currency',
      'region',
      'population',
      'borders',
      'neighbour',
      'language',
      'populous',
      'languages',
      'truefalse',
      'numeric',
    ],
    orientToRegion: false,
    pinpointAttempts: SINGLE_PINPOINT_ATTEMPT,
    goldenRoundsPerMatch: SCORING.GOLDEN_ROUNDS_PER_MATCH,
  },
};

/**
 * Does rung 1 of a COUNTRY round describe the country instead of naming it?
 *
 * One predicate, so the ladder builder, the reading-time tests and the screen
 * that explains the tier cannot disagree about which tier is the clue tier.
 * Cities and US States have their own name-or-clue split (by tier, in their
 * builders) and are unaffected by this.
 */
export function countryPromptIsClue(tier: Tier): boolean {
  return tier === 'surveyor';
}

export function isTier(v: unknown): v is Tier {
  return typeof v === 'string' && (TIERS as readonly string[]).includes(v);
}

export function isProbeType(v: unknown): v is ProbeType {
  return typeof v === 'string' && (PROBE_TYPES as readonly string[]).includes(v);
}

export function tierConfig(tier: Tier): TierConfig {
  return TIER_CONFIG[tier];
}

/** The pinpoint attempt allowance for a tier. One lookup, so callers agree. */
export function pinpointAttemptPolicy(tier: Tier): PinpointAttemptPolicy {
  return TIER_CONFIG[tier].pinpointAttempts;
}

// ---------------------------------------------------------------------------
// The guided round's clock
// ---------------------------------------------------------------------------

/**
 * THE TUTORIAL RUNS SLOW, AND ONLY THE TUTORIAL, AND ONLY ITS FIRST ROUND.
 *
 * `coach.ts` used to promise that a coached match was "timed and driven
 * exactly like any other practice match", on the reasoning that a tutorial
 * played at a pace we do not ship teaches a game we do not ship. That
 * reasoning is right and this does not abandon it — it narrows it.
 *
 * Explorer gives 15s to answer and 8s to decide. That is a fair clock for
 * somebody who knows what the screen is asking. It is not enough time to read
 * a coaching line, look at a globe you have never been shown before, and make
 * a bet whose terms you are reading for the first time — playtesters said so
 * plainly, and a tip nobody can finish reading is not a tip. A tutorial you
 * cannot read teaches nothing at all, which is strictly worse than one that
 * teaches a slow version of the real thing.
 *
 * SO THE SLOW CLOCK IS FENCED TO ROUND ONE. That is exactly where the coaching
 * lines are (`coachLine` returns null for the loop beats after round 0), so
 * every round the player plays without a tip in front of them runs at the real
 * speed, inside the same match, immediately after. They feel the true pace
 * before they ever leave the tutorial, and the round-end line tells them the
 * change is coming.
 *
 * ONE SOURCE, THREE READERS. The rules machine sets deadlines, the practice
 * driver expires them, and the HUD draws the ring from the round payload. All
 * three go through `roundClockMs`, because a ring that disagrees with the
 * authority about when a round ends is the worst bug this file could produce:
 * it looks exactly like the game cheating.
 */
export const COACHED_CLOCK = {
  /** Long enough to read four lines and still find a country you half-know. */
  secondsPerRung: 60,
  /** The decision line is the longest one in the tutorial and the most important. */
  decisionSeconds: 45,
} as const;

export interface RoundClockMs {
  readonly rungMs: number;
  readonly decisionMs: number;
}

/**
 * Is this the round that gets the slow clock?
 *
 * The SAME predicate `coachLine` applies to its loop beats, kept here so the
 * clock and the coaching cannot disagree about which round is the tutorial.
 * A round with a tip on it and a 15s timer, or a silent round with 60s, would
 * each be worse than having neither.
 */
export function isCoachedRound(coached: boolean, roundIdx: number): boolean {
  return coached && roundIdx === 0;
}

/**
 * PACE IS NOT DIFFICULTY, and keeping them apart is the whole point.
 *
 * A tier decides WHAT you are asked -- how obscure the country, how hard the
 * probe. Pace decides how long you get to answer it. They look similar from a
 * settings screen and they are not remotely the same axis: somebody who knows
 * their geography cold but reads slowly wants Hardest at a relaxed pace, and a
 * child racing a friend wants Easiest at full speed. Folding pace into the
 * tier would have made both of those impossible and called it simplicity.
 *
 * The reason this exists is a real one. A game whose whole loop is a clock is
 * a game that quietly excludes anybody who cannot beat the clock -- people who
 * know more geography than the person who built it and simply need longer to
 * point at it. Nothing about the questions has to change for them.
 */
/**
 * THE TIER EVERY DAILY IS PLAYED AT.
 *
 * A daily is worth something because everybody got the same puzzle -- that is
 * the entire mechanic, and it is what makes a score worth saying out loud. The
 * daily used to be per tier, so "I got 8,190" was three different claims
 * depending on which of three boards you were on, and adding a daily per mode
 * would have made it nine.
 *
 * Three, one per mode, all at Standard. The schema still keys on tier (see
 * migration 008) so this is a decision about what we OFFER rather than what
 * can be stored, and changing our mind later costs a constant rather than a
 * migration.
 */
export const DAILY_TIER: Tier = 'navigator';

export type Pace = 'standard' | 'relaxed';

export const PACES: readonly Pace[] = ['standard', 'relaxed'];

export function isPace(v: unknown): v is Pace {
  return v === 'standard' || v === 'relaxed';
}

/**
 * The relaxed clock.
 *
 * IT HAS ALREADY DRIFTED FROM `COACHED_CLOCK`, which is why they were separate
 * constants from the day relaxed shipped even though they started identical.
 * They answer different questions: coached buys time to READ four lines of
 * coaching, relaxed buys time to THINK about a map, and one constant serving
 * both could only ever have been right for one of them.
 *
 * THE DECISION WINDOW IS THE HALF THAT NEEDED MORE. Reported from play: the
 * answer window was comfortable and bank-or-push still was not. That fits what
 * the two windows are for. Finding a country is recognition, and either you
 * know roughly where Peru is or you do not; the decision is arithmetic against
 * a pot, and arithmetic is exactly the thing that does not get faster when
 * somebody is being hurried. Sixty seconds either side now, so the round has
 * one pace rather than a slow half and a brisk one.
 */
export const RELAXED_CLOCK = {
  secondsPerRung: 60,
  decisionSeconds: 60,
} as const;

/**
 * The answer and decision windows for one round, in milliseconds.
 *
 * COACHED WINS OVER RELAXED, on the one round it applies to. Both are slow
 * clocks and the tutorial's is the one with words on the screen to read; a
 * relaxed player meeting the guided round should not get a FASTER first round
 * than a standard player would.
 */
export function roundClockMs(
  tier: Tier,
  coachedRound: boolean,
  pace: Pace = 'standard',
): RoundClockMs {
  const cfg = TIER_CONFIG[tier];
  const seconds = coachedRound ? COACHED_CLOCK : pace === 'relaxed' ? RELAXED_CLOCK : cfg;
  return {
    rungMs: seconds.secondsPerRung * 1000,
    decisionMs: seconds.decisionSeconds * 1000,
  };
}

// ---------------------------------------------------------------------------
// Reading time for a prompt you have to read before you can start looking
// ---------------------------------------------------------------------------

/**
 * The longest a rung-1 prompt can be and still just be a NAME.
 *
 * Measured against the shipped data rather than guessed. The longest state or
 * city name in any pool is 16 characters; the shortest clue is 42. Anything
 * between them is nobody's, which is what makes a threshold honest here
 * instead of arbitrary, and `readingtime.test.ts` re-measures both pools so
 * the gap cannot quietly close.
 */
export const NAME_PROMPT_MAX_CHARS = 28;

/**
 * Extra seconds a rung-1 prompt earns for being a sentence.
 *
 * WHY RUNG 1 AND NOTHING ELSE. Every other rung puts its prompt above four
 * options that are themselves the answer space: you read the question and the
 * candidates together, in one place, and the clock is sized for that. Rung 1
 * puts a sentence above a GLOBE. You have to finish reading before the search
 * can even begin, and the search is the part the clock was measured for.
 *
 * At cartographer the state pool is clues only, on an 8-second rung. Testers
 * called it "borderline impossible for a real human", which is the correct
 * description of being asked to read 83 characters, work out which state is
 * being described, and find it, in eight seconds.
 */
export const PROMPT_READING_SECONDS = 5;

/**
 * How much longer rung 1 runs because its prompt has to be read.
 *
 * A FLAT BONUS RATHER THAN A RATE, because the data is not a spectrum: prompts
 * here are either a name or a full sentence, with nothing in between. A rate
 * would dress an arbitrary constant up as a model and still land in the same
 * place.
 *
 * COMPUTED BY THE AUTHORITY AND SENT, never recomputed by the client. The room
 * and the practice driver call this; the number rides `round.arm` and the
 * client takes it as given. Two sides deriving the same clock from the same
 * string would work right up until one of them normalised whitespace.
 */
export function pinpointReadSeconds(prompt: string): number {
  return prompt.trim().length > NAME_PROMPT_MAX_CHARS ? PROMPT_READING_SECONDS : 0;
}
